import React from 'react';
import { Card, Badge } from '@/components/ui';
import { Users, AlertTriangle, Clock } from 'lucide-react';
import { formatTeamWeeks, formatPercent } from '@/utils/formatters';

interface Team {
  teamId: string;
  teamName: string;
  memberCount: number;
  totalCapacity: number;
  allocatedCapacity: number;
  utilizationPercentage: number;
  overAllocation: number;
}

interface CapacityUtilization {
  totalCapacity: number;
  allocatedCapacity: number;
  availableCapacity: number;
  utilizationPercentage: number;
  teamCount: number;
  teams: Team[];
  quarterlyTrend: Array<{
    quarter: number;
    utilization: number;
    capacity: number;
  }>;
}

interface CapacityUtilizationChartsProps {
  capacityUtilization: CapacityUtilization | null;
  isLoading: boolean;
}

const CapacityUtilizationCharts: React.FC<CapacityUtilizationChartsProps> = ({
  capacityUtilization,
  isLoading,
}) => {
  const getUtilizationColor = (percentage: number) => {
    if (percentage > 100) return 'text-status-error';
    if (percentage > 90) return 'text-status-warning';
    if (percentage > 70) return 'text-status-success';
    return 'text-accent-blue';
  };

  const getUtilizationBadge = (percentage: number) => {
    if (percentage > 100) return <Badge variant="danger">Over-allocated</Badge>;
    if (percentage > 90) return <Badge variant="warning">High Utilization</Badge>;
    if (percentage > 70) return <Badge variant="success">Good Utilization</Badge>;
    return <Badge variant="info">Under-utilized</Badge>;
  };

  const getUtilizationBarColor = (percentage: number) => {
    if (percentage > 100) return 'bg-status-error';
    if (percentage > 90) return 'bg-status-warning';
    if (percentage > 70) return 'bg-status-success';
    return 'bg-blue-500';
  };

  if (isLoading) {
    return (
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-muted/50 rounded w-1/2 mb-4"></div>
          <div className="space-y-4">
            <div className="h-20 bg-muted/50 rounded"></div>
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 bg-muted/50 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (!capacityUtilization) {
    return (
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Capacity Utilization</h3>
          <div className="text-center text-muted-foreground py-8">
            No capacity utilization data available
          </div>
        </div>
      </Card>
    );
  }

  const overAllocatedTeams = capacityUtilization.teams.filter(t => t.utilizationPercentage > 100);
  const highUtilizationTeams = capacityUtilization.teams.filter(t => t.utilizationPercentage > 90 && t.utilizationPercentage <= 100);

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Capacity Utilization</h3>
            <p className="text-sm text-muted-foreground">
              Team capacity allocation and utilization
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {getUtilizationBadge(capacityUtilization.utilizationPercentage)}
            <span className={`text-lg font-bold ${getUtilizationColor(capacityUtilization.utilizationPercentage)}`}>
              {formatPercent(capacityUtilization.utilizationPercentage)}
            </span>
          </div>
        </div>

        {/* Overall Capacity Summary */}
        <div className="bg-muted rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-lg font-bold text-foreground">
                {formatTeamWeeks(capacityUtilization.totalCapacity)}
              </div>
              <div className="text-xs text-muted-foreground">Total Capacity</div>
            </div>
            <div>
              <div className="text-lg font-bold text-foreground">
                {formatTeamWeeks(capacityUtilization.allocatedCapacity)}
              </div>
              <div className="text-xs text-muted-foreground">Allocated</div>
            </div>
            <div>
              <div className={`text-lg font-bold ${
                capacityUtilization.availableCapacity < 0 ? 'text-status-error' : 'text-status-success'
              }`}>
                {formatTeamWeeks(capacityUtilization.availableCapacity)}
              </div>
              <div className="text-xs text-muted-foreground">Available</div>
            </div>
            <div>
              <div className="text-lg font-bold text-foreground">
                {capacityUtilization.teamCount}
              </div>
              <div className="text-xs text-muted-foreground">Teams</div>
            </div>
          </div>

          {/* Overall Utilization Bar */}
          <div className="mt-4">
            <div className="w-full bg-muted/50 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${getUtilizationBarColor(capacityUtilization.utilizationPercentage)}`}
                style={{ width: `${Math.min(capacityUtilization.utilizationPercentage, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Team-by-Team Breakdown */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground">Team Utilization</h4>
          
          {capacityUtilization.teams
            .sort((a, b) => b.utilizationPercentage - a.utilizationPercentage)
            .map((team) => (
              <div key={team.teamId} className="bg-muted rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <Users className="w-4 h-4 text-accent-blue" />
                      <span className="font-medium text-foreground">{team.teamName}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {team.memberCount} members
                    </span>
                    {team.utilizationPercentage > 100 && (
                      <Badge variant="danger">Over-allocated</Badge>
                    )}
                  </div>
                  <div className="text-right">
                    <div className={`font-medium ${getUtilizationColor(team.utilizationPercentage)}`}>
                      {formatPercent(team.utilizationPercentage)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatTeamWeeks(team.allocatedCapacity)} / {formatTeamWeeks(team.totalCapacity)}
                    </div>
                  </div>
                </div>

                {/* Team Utilization Bar */}
                <div className="space-y-2">
                  <div className="w-full bg-muted/50 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getUtilizationBarColor(team.utilizationPercentage)}`}
                      style={{ width: `${Math.min(team.utilizationPercentage, 100)}%` }}
                    />
                  </div>

                  {team.overAllocation > 0 && (
                    <div className="flex items-center space-x-2 text-xs text-status-error">
                      <AlertTriangle className="w-3 h-3" />
                      <span>Over-allocated by {formatTeamWeeks(team.overAllocation)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>

        {/* Quarterly Trend */}
        {capacityUtilization.quarterlyTrend && capacityUtilization.quarterlyTrend.length > 0 && (
          <div className="mt-6 pt-6 border-t border-border">
            <h4 className="font-medium text-foreground mb-4">Quarterly Utilization Trend</h4>
            <div className="grid grid-cols-4 gap-4">
              {capacityUtilization.quarterlyTrend.map((quarter) => (
                <div key={quarter.quarter} className="text-center">
                  <div className="bg-muted rounded-lg p-3">
                    <div className="text-sm text-muted-foreground mb-1">Q{quarter.quarter}</div>
                    <div className={`text-lg font-bold ${getUtilizationColor(quarter.utilization)}`}>
                      {formatPercent(quarter.utilization)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatTeamWeeks(quarter.capacity)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Utilization Alerts */}
        {(overAllocatedTeams.length > 0 || highUtilizationTeams.length > 0) && (
          <div className="mt-6 pt-6 border-t border-border">
            <h4 className="font-medium text-foreground mb-4">Utilization Alerts</h4>
            <div className="space-y-3">
              {overAllocatedTeams.length > 0 && (
                <div className="flex items-center space-x-2 p-3 bg-status-error/10 border border-status-error/30 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-status-error" />
                  <span className="text-sm text-status-error font-medium">
                    {overAllocatedTeams.length} team(s) over-allocated
                  </span>
                  <span className="text-xs text-muted-foreground">
                    - Requires immediate attention
                  </span>
                </div>
              )}
              
              {highUtilizationTeams.length > 0 && (
                <div className="flex items-center space-x-2 p-3 bg-status-warning/10 border border-yellow-500/20 rounded-lg">
                  <Clock className="w-4 h-4 text-status-warning" />
                  <span className="text-sm text-status-warning font-medium">
                    {highUtilizationTeams.length} team(s) at high utilization
                  </span>
                  <span className="text-xs text-muted-foreground">
                    - Monitor for potential over-allocation
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default CapacityUtilizationCharts;